import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, Zap, ArrowRight, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const Pricing = () => {
  const { t } = useTranslation();
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  
  const included = [
    t('pricingFeatures.included.btc'),
    t('pricingFeatures.included.eth'),
    t('pricingFeatures.included.altcoins'),
    t('pricingFeatures.included.futures'),
    t('pricingFeatures.included.levels'),
    t('pricingFeatures.included.analysis'),
    t('pricingFeatures.included.guidelines'),
    t('pricingFeatures.included.support'),
  ];

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`,
        },
      });

      if (error) throw error;

      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast({
        title: "Error",
        description: "Failed to start checkout. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <SEO 
        title="Pricing & Plans - $99/Month for Elite Crypto Signals | Oraculus:X"
        description="Get access to premium crypto signals for just $99/month. Compare plans and save $400-500 vs individual subscriptions."
      />
      <div className="fixed inset-0 bg-gradient-mesh animate-mesh-move opacity-40" />
      <div className="fixed inset-0 bg-gradient-opal-radial animate-gradient-shift" style={{ backgroundSize: '200% 200%' }} />
      
      <main className="relative z-10 pt-20 md:pt-32">
        <section className="py-20">
          <div className="container px-4">
            <Card className="p-8 md:p-12 bg-gradient-primary relative overflow-hidden max-w-4xl mx-auto">
              <div className="absolute inset-0 bg-card/80 backdrop-blur-2xl" />
              <div className="relative z-10">
                <h1 className="text-3xl md:text-4xl font-bold text-center mb-8">
                  Complete Premium Package — $99/Month
                </h1>
                <div className="grid md:grid-cols-2 gap-4 mb-8">
                  {included.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/20 backdrop-blur-sm hover:bg-secondary/30 transition-colors">
                      <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-base">{item}</span>
                    </div>
                  ))}
                </div>
                <div className="pt-8 border-t border-border/50 text-center space-y-4 px-4">
                  <div className="space-y-2">
                    <p className="text-sm text-muted-foreground">
                      🎁 <span className="font-semibold text-primary">7-Day Free Trial</span> — No credit card required
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Cancel anytime • Start trading smarter today
                    </p>
                  </div>
                  <Button 
                    size="lg" 
                    className="bg-gradient-primary hover:shadow-glow-primary transition-all duration-300 text-base md:text-lg px-8 md:px-16 py-4 md:py-6 group w-full sm:w-auto"
                    onClick={handleSubscribe}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Zap className="w-5 h-5" />
                        {user ? "Get Premium — Start Free Trial" : "Sign Up to Subscribe"}
                        <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Pricing;
